import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UserCheck,
  Plus,
  UserPlus,
  UserCog,
  ExternalLink,
  UtensilsCrossed,
  Salad,
  Coffee,
  IceCream,
  Martini,
  Wine,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { getAdminRestaurants, AdminRestaurant, setAdminPairmeReview, type PairmeReviewFilter, type AdminPairmeReview, type AdminPairmeShared, type PairmeSharedFilter } from '../../services/adminApi';
import { handleImpersonate } from '../../utils/impersonate';
import { AdminEmptyState } from './_adminEmptyState';
import { AddRestaurantModal } from './_addRestaurantModal';
import { ManageAdminDrawer } from './_manageAdminDrawer';
import { ADMIN_PAGE_FRAME, ADMIN_PAGE_FRAME_STYLE } from '../../components/admin/adminPageFrame';

// Menu coverage is presence, not a quality score: six recognized menu kinds,
// each rendered as an icon alone, with the menu type on hover and in the
// accessible name (Moose, production walk 2026-09-08 - the visible words made
// the row too wide to read). The `main` kind maps onto the dinner plate (in the
// trade a main menu and a dinner menu are the same thing); the plate lights
// once even if a restaurant carries both.
const MENU_KINDS: Array<{ kinds: string[]; label: string; Icon: typeof UtensilsCrossed }> = [
  { kinds: ['dinner', 'main'], label: 'Dinner', Icon: UtensilsCrossed },
  { kinds: ['lunch'], label: 'Lunch', Icon: Salad },
  { kinds: ['brunch'], label: 'Brunch', Icon: Coffee },
  { kinds: ['dessert'], label: 'Dessert', Icon: IceCream },
  { kinds: ['drinks'], label: 'Drinks', Icon: Martini },
  { kinds: ['wine'], label: 'Wine', Icon: Wine },
];

// Known data_flags tokens get a plain-language label + explanatory title.
// Unknown tokens render as-is, still in a neutral badge.
const DATA_FLAG_LABELS: Record<string, { label: string; title: string }> = {
  place_id_conflict: {
    label: 'Place ID conflict',
    title: 'Place ID is allowed to be shared across distinct businesses; these are not merged.',
  },
  place_id_unresolved: {
    label: 'Place ID unresolved',
    title: 'A Place ID lookup for this restaurant did not resolve to a single business.',
  },
};

function parseDataFlags(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(/[,\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

// The identity (Name) column is pinned to the left edge of the horizontal
// scrollport. This table is ~1,991px wide at a 639px viewport, so Coverage,
// Data Flags and Actions all sit offscreen; before this, scrolling right to
// reach them scrolled the restaurant name away too, and the operator was left
// reading anonymous rows. Header and body share the same `left-0` offset and
// the same declared classes so the pinned column cannot drift out of
// alignment. z-index and an opaque background are load-bearing: without them
// the scrolled-under cells show through the pinned one.
const STICKY_IDENTITY = 'sticky left-0 border-r border-gray-200';
// Header sits above the body cells so the two never composite over each other
// at the top-left corner, and carries the header row's own grey.
const STICKY_IDENTITY_HEAD = `${STICKY_IDENTITY} z-20 bg-gray-50`;
// Body cells are opaque white, and repeat the row's hover grey themselves
// (via `group` on the row) because a sticky cell is painted separately from
// the <tr> background it would otherwise inherit.
const STICKY_IDENTITY_CELL = `${STICKY_IDENTITY} z-10 bg-white group-hover:bg-gray-50`;

// One screenful at a time. The endpoint caps per_page at 200.
const PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// The states the estate actually carries today. The server matches
// restaurants.state exactly and ignores anything that is not a valid region.
const STATE_FILTERS = ['CO', 'MN', 'MA'];

type SortField = 'name' | 'city' | 'status' | 'contact_count' | 'created_at';
type SortDir = 'asc' | 'desc';

export function QMAdminRestaurants() {
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // PAIRME REVIEW FILTER (Moose, 2026-09-30): server-side, like search.
  const [pairmeFilter, setPairmeFilter] = useState<PairmeReviewFilter | ''>('');
  // STATE and CRAWLED filters: server-side, so an operator can see a finished
  // crawler load (for example every crawled CO row) across all pages.
  const [stateFilter, setStateFilter] = useState('');
  const [crawledOnly, setCrawledOnly] = useState(false);
  // PAIRME SENT FILTER (Moose, 2026-11-03): server-side, like the others.
  const [sharedFilter, setSharedFilter] = useState<PairmeSharedFilter | ''>('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [impersonating, setImpersonating] = useState<string | null>(null);

  // Server-side paging. The unpaginated response is 5,865,033 bytes across
  // 10,333 restaurants and timed this screen out.
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total_count: 0, total_pages: 0 });
  // Search is debounced so a keystroke is not a request. It is matched
  // server-side: filtering the current page in the browser would report a
  // restaurant on page 7 as not existing.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Feature 1: Add Restaurant modal
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Feature 2: Manage Admin drawer
  const [manageAdminTarget, setManageAdminTarget] = useState<AdminRestaurant | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const loadRestaurants = useCallback(async () => {
    setLoading(true);
    const res = await getAdminRestaurants({
      page,
      per_page: PER_PAGE,
      q: debouncedSearch || undefined,
      sort: sortField,
      dir: sortDir,
      pairme_review: pairmeFilter || undefined,
      state: stateFilter || undefined,
      crawled: crawledOnly || undefined,
      pairme_shared: sharedFilter || undefined,
    });
    if (res.data) {
      setRestaurants(res.data.restaurants);
      setMeta(res.data.meta);
      setError(null);
    } else {
      setError(res.error || 'Failed to load');
    }
    setLoading(false);
  }, [page, debouncedSearch, sortField, sortDir, pairmeFilter, stateFilter, crawledOnly, sharedFilter]);

  // The flag is clickable: needs review -> reviewed, and back. Only the
  // clicked row changes; the list is not reloaded.
  const toggleReview = async (r: AdminRestaurant) => {
    if (!r.pairme_review) return;
    const res = await setAdminPairmeReview(r.id, r.pairme_review.state !== 'reviewed');
    if (res.data) {
      const next = res.data.pairme_review;
      setRestaurants((prev) => prev.map((x) => (x.id === r.id ? { ...x, pairme_review: next } : x)));
    }
  };

  useEffect(() => {
    loadRestaurants();
  }, [loadRestaurants]);

  const toggleSort = (field: SortField) => {
    // Reordering the whole result set can move any row onto page 1, so staying
    // on the current page number after a sort would show an arbitrary slice.
    setPage(1);
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="text-gray-300" />;
    return sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Filtering and sorting are server-side; `restaurants` is already the page
  // the operator asked for.

  return (
    <div className={ADMIN_PAGE_FRAME} style={ADMIN_PAGE_FRAME_STYLE}>
      <h1 className="text-2xl font-bold text-[#2A2A2A] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
        Restaurants & Groups
      </h1>

      {/* On a phone the search box takes its own line and the filters sit
          under it (Moose's walk, 2026-10-11); from sm up it is one row. */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div data-testid="restaurants-search" className="relative basis-full sm:basis-auto sm:flex-1 sm:max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search restaurants, groups..."
            className="pl-9"
          />
        </div>
        <select
          aria-label="PairMe review"
          data-testid="pairme-review-filter"
          value={pairmeFilter}
          onChange={(e) => { setPairmeFilter(e.target.value as PairmeReviewFilter | ''); setPage(1); }}
          className="border border-gray-200 rounded-md px-2 py-1.5 text-sm"
        >
          <option value="">All restaurants</option>
          <option value="any_pairme">On PairMe</option>
          <option value="needs_review">PairMe: needs review</option>
          <option value="reviewed">PairMe: reviewed</option>
        </select>
        <select
          aria-label="State"
          data-testid="state-filter"
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-md px-2 py-1.5 text-sm"
        >
          <option value="">All states</option>
          {STATE_FILTERS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            data-testid="crawled-filter"
            checked={crawledOnly}
            onChange={(e) => { setCrawledOnly(e.target.checked); setPage(1); }}
          />
          Crawled only
        </label>
        <select
          aria-label="PairMe sent"
          data-testid="pairme-shared-filter"
          value={sharedFilter}
          onChange={(e) => { setSharedFilter(e.target.value as PairmeSharedFilter | ''); setPage(1); }}
          className="border border-gray-200 rounded-md px-2 py-1.5 text-sm"
        >
          <option value="">PairMe sent or not</option>
          <option value="yes">PairMe sent</option>
          <option value="no">PairMe not sent</option>
        </select>
        <span className="text-xs text-gray-400">
          {meta.total_count} restaurants
          {meta.total_pages > 1 && ` (page ${meta.page} of ${meta.total_pages})`}
        </span>
        <Button
          onClick={() => setAddModalOpen(true)}
          className="ml-auto bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white flex items-center gap-1.5"
        >
          <Plus size={15} />
          Add Restaurant
        </Button>
      </div>

      {loading && <p className="text-sm text-gray-400 py-8">Loading...</p>}
      {error && <p className="text-sm text-red-500 py-8">{error}</p>}

      {!loading && !error && restaurants.length === 0 && <AdminEmptyState label="restaurants" />}

      {!loading && restaurants.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead
                    data-testid="restaurants-identity-head"
                    className={`cursor-pointer ${STICKY_IDENTITY_HEAD}`}
                    onClick={() => toggleSort('name')}
                  >
                    <div className="flex items-center gap-1">Name <SortIcon field="name" /></div>
                  </TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('city')}>
                    <div className="flex items-center gap-1">City <SortIcon field="city" /></div>
                  </TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead>Place ID</TableHead>
                  <TableHead>Source State</TableHead>
                  <TableHead
                    title="Icons show which menus we have. Presence, not a quality score."
                  >
                    Menu Coverage
                  </TableHead>
                  <TableHead>Data Flags</TableHead>
                  <TableHead title="Auto-paired restaurants go live flagged. Click the flag once you have looked at the pairings.">PairMe</TableHead>
                  <TableHead title="Percent of the eight launch checklist steps done. $ means the restaurant is paying.">Launch</TableHead>
                  <TableHead title="PairMe info emailed (by a QM admin or a rep) or a rep referral logged.">PairMe sent</TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => toggleSort('contact_count')}>
                    <div className="flex items-center justify-end gap-1">Contacts <SortIcon field="contact_count" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('status')}>
                    <div className="flex items-center gap-1">Status <SortIcon field="status" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('created_at')}>
                    <div className="flex items-center gap-1">Created <SortIcon field="created_at" /></div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {restaurants.map((r) => {
                  const dataFlags = parseDataFlags(r.data_flags);
                  return (
                  <TableRow key={r.id} className="group hover:bg-gray-50">
                    <TableCell
                      data-testid={`restaurants-identity-cell-${r.id}`}
                      className={`font-medium ${STICKY_IDENTITY_CELL}`}
                    >
                      <Link to={`/qm-admin/restaurants/${r.id}`} className="text-[#2A2A2A] hover:text-[#7FAEC2] hover:underline">
                        {r.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.restaurant_group ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-[#7FAEC2]/10 text-[#7FAEC2] font-medium">
                          {r.restaurant_group.name}
                        </span>
                      ) : (
                        <span className="text-gray-400">Ungrouped</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{r.city || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{r.state || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {r.website ? (
                        <a
                          href={r.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#7FAEC2] hover:text-[#6A9AB0] hover:underline max-w-[160px] truncate"
                          title={r.website}
                        >
                          <ExternalLink size={12} className="shrink-0" />
                          <span className="truncate">{r.website}</span>
                        </a>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono max-w-[140px] truncate text-gray-500" title={r.google_place_id || undefined}>
                      {r.google_place_id || <span className="text-gray-300">-</span>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {r.source_state || <span className="text-gray-300">-</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {MENU_KINDS.map(({ kinds, label, Icon }) => {
                          const present = kinds.some((k) => r.menu_coverage.includes(k));
                          // Presence is carried visually by opacity and colour alone, so
                          // sighted and non-sighted operators were reading different data:
                          // the accessible name used to be just "Dinner" whether we held a
                          // dinner menu or not. Now that the visible word is gone too, the
                          // icon is the entire visible control, and two separate affordances
                          // have to carry the menu type: `title` for a sighted operator on
                          // hover, `aria-label` for everyone else. A tooltip is NOT an
                          // accessible name, so dropping the aria-label in favour of the
                          // title would re-open the exact defect this replaced.
                          const indicatorName = `${label} menu: ${present ? 'present' : 'not present'}`;
                          return (
                            <span
                              key={label}
                              role="img"
                              aria-label={indicatorName}
                              title={indicatorName}
                              className={`inline-flex ${
                                present ? 'text-gray-600 opacity-100' : 'text-gray-400 opacity-30'
                              }`}
                            >
                              <Icon size={16} aria-hidden="true" />
                            </span>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      {dataFlags.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {dataFlags.map((flag) => {
                            const known = DATA_FLAG_LABELS[flag];
                            return (
                              <Badge
                                key={flag}
                                variant="secondary"
                                title={known ? known.title : flag}
                              >
                                {known ? known.label : flag}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell><PairmeReviewFlag review={r.pairme_review} onToggle={() => toggleReview(r)} /></TableCell>
                    {/* LAUNCH CHECKLIST, reader of onboarding_percent and paying (QuoteMe
                        Admin::RestaurantsController#serialize_index). Moose, 2026-10-15:
                        "% of the SOP done for each restaurant so we know where they're at.
                        plus a $ symbol when they're paying." */}
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`restaurants-launch-${r.id}`}>
                      {/* The key is QM admin only (Moose, 2026-10-15: "no just qm
                          admins"); absent means not shown to this viewer, never 0. */}
                      {r.onboarding_percent == null ? (
                        <span className="text-gray-300">-</span>
                      ) : (
                        <span className={r.onboarding_percent === 100 ? 'font-semibold text-green-700' : 'text-[#2A2A2A]'}>
                          {r.onboarding_percent}%
                        </span>
                      )}
                      {r.paying && (
                        <span
                          className="ml-2 inline-block px-1.5 rounded text-xs font-semibold bg-green-100 text-green-700"
                          title="Paying"
                          aria-label="Paying"
                          data-testid={`restaurants-paying-${r.id}`}
                        >
                          $
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`restaurants-shared-${r.id}`}>
                      <PairmeSharedChip shared={r.pairme_shared} />
                    </TableCell>
                    <TableCell className="text-sm text-right">{r.contact_count}</TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Manage Admin button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-[#7FAEC2] hover:text-[#6A9AB0]"
                          onClick={() => setManageAdminTarget(r)}
                          aria-label={r.restaurant_admin_id ? `Manage the admin user for ${r.name}` : `Add an admin user for ${r.name}`}
                          title={r.restaurant_admin_id ? `Manage the admin user for ${r.name}` : `Add an admin user for ${r.name}`}
                        >
                          {r.restaurant_admin_id ? (
                            <UserCog size={14} className="mr-1" />
                          ) : (
                            <UserPlus size={14} className="mr-1" />
                          )}
                          {r.restaurant_admin_id ? 'Manage Admin' : 'Add Admin'}
                        </Button>

                        {/* Impersonate button.
                            This does NOT sign you in as the restaurant admin.
                            admin_user_id is serialized as `created_by_user ||
                            first rep of the first attached distributor`
                            (restaurants_controller.rb:457), so the target is
                            whoever happened to create the row, and it is a
                            different person from restaurant_admin_name in the
                            common case. The control used to read a bare
                            "Impersonate" and name nobody at all, which left the
                            operator to assume the restaurant's own admin.
                            Naming the actual target is the honest half; the
                            question of whether this SHOULD target that user is
                            a behaviour change and sits with Moose. */}
                        {r.admin_user_id ? (
                          <div className="flex flex-col items-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-[#7FAEC2] hover:text-[#6A9AB0]"
                              disabled={impersonating === r.admin_user_id}
                              onClick={() => handleImpersonate(r.admin_user_id!, r.admin_user_name || r.name, setImpersonating, setError)}
                              aria-label={`Sign in as ${r.admin_user_name || 'the user who created this restaurant'}`}
                              title={`Sign in as ${r.admin_user_name || 'the user who created this restaurant'}`}
                            >
                              <UserCheck size={14} className="mr-1" />
                              {impersonating === r.admin_user_id ? 'Switching...' : 'Sign in as'}
                            </Button>
                            <span className="text-[11px] text-gray-400 pr-2 max-w-[12rem] truncate">
                              {r.admin_user_name || 'unnamed user'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 px-2">No user</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {!loading && meta.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-400">
            Showing {(meta.page - 1) * meta.per_page + 1} to{' '}
            {Math.min(meta.page * meta.per_page, meta.total_count)} of {meta.total_count}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-gray-500">
              Page {meta.page} of {meta.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page >= meta.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add Restaurant modal */}
      <AddRestaurantModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={() => {
          loadRestaurants();
        }}
      />

      {/* Manage Admin drawer */}
      <ManageAdminDrawer
        open={Boolean(manageAdminTarget)}
        restaurant={manageAdminTarget}
        onClose={() => setManageAdminTarget(null)}
        onAssigned={() => {
          loadRestaurants();
        }}
      />
    </div>
  );
}

// THE PAIRME REVIEW FLAG (Moose, 2026-09-30). An auto-paired restaurant goes
// live flagged "Needs review"; clicking it marks it reviewed (and clicking
// again flags it back). A hand-authored venue shows "Hand-paired", not
// clickable. No PairMe venue shows a dash.
export function PairmeReviewFlag({ review, onToggle }: { review?: AdminPairmeReview | null; onToggle: () => void }) {
  if (!review) return <span className="text-sm text-gray-300">-</span>;
  if (review.state === null) {
    return <span data-testid="pairme-review-flag" className="text-xs text-gray-500">Hand-paired</span>;
  }
  const reviewed = review.state === 'reviewed';
  const when = review.reviewed_at ? new Date(review.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return (
    <button
      type="button"
      data-testid="pairme-review-flag"
      onClick={onToggle}
      title={reviewed ? `Reviewed${review.reviewed_by_name ? ` by ${review.reviewed_by_name}` : ''}${when ? `, ${when}` : ''}. Click to flag it again.` : 'Auto-paired. Click once you have looked at the pairings.'}
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${reviewed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-300'}`}
    >
      {reviewed ? 'Reviewed' : 'Needs review'}
    </button>
  );
}

/** "Sent" with what and when on hover; a dash when nothing was sent. */
export function pairmeSharedTitle(s: AdminPairmeShared): string {
  const parts: string[] = [];
  if (s.info_emails > 0) parts.push(`${s.info_emails} info email${s.info_emails === 1 ? '' : 's'}`);
  if (s.referrals > 0) parts.push(`${s.referrals} rep referral${s.referrals === 1 ? '' : 's'}`);
  const when = s.last_at ? new Date(s.last_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  return `PairMe sent: ${parts.join(', ')}${when ? `, last ${when}` : ''}`;
}

export function PairmeSharedChip({ shared }: { shared?: AdminPairmeShared | null }) {
  if (!shared) return <span className="text-gray-300">-</span>;
  const title = pairmeSharedTitle(shared);
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-[#E8F2F6] text-[#2F6F86]"
      title={title}
      aria-label={title}
    >
      Sent
    </span>
  );
}
